import { useMemo, useState, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';

import { useBoolean } from 'src/hooks/use-boolean';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { firebaseAuth } from 'src/libs/firebase/firebase';
import { AuthUserType, SignInResponseType } from 'src/type/auth';
import { toastError } from 'src/utils/format-error';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/routes/paths';
import { AuthContext } from './auth-context';
import { setSession, isValidToken } from './utils';

import { ACCESS_TOKEN_KEY } from '../config';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState<AuthUserType | null>(null);
  const loading = useBoolean(true);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const { data: user } = await axios.get<AuthUserType>(endpoints.auth.me);

        if (user?.role !== 'ADMIN') {
          throw new Error('Permission denied');
        }

        setAuthUser(user);
      } else {
        setAuthUser(null);
      }
    } catch (error) {
      setAuthUser(null);
    } finally {
      loading.onFalse();
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const idToken = await userCredential.user.getIdToken();

      const { data } = await axios.post<SignInResponseType>(endpoints.auth.login, { idToken });

      const { user, accessToken } = data;

      if (!accessToken || user?.role !== 'ADMIN') {
        throw new Error('Permission denied');
      }

      setSession(accessToken);
      setAuthUser(user);
    } catch (error) {
      toastError(error);
    }
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    setAuthUser(null);
    navigate(paths.auth.login, { replace: true });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = authUser ? 'authenticated' : 'unauthenticated';

  const status = loading.value ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: authUser,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      logout,
    }),
    [login, logout, authUser, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
